import { CreateUserRequestBody, UpdateUserRequestBody, type User, type UserRole } from '@engagement-web-app/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type z from 'zod';
import { Button } from '@/components/Button';
import { Dialog, DialogButtonGroup, DialogClose } from '@/components/Dialog';
import { Form } from '@/components/Form';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';

const addUserFormSchema = CreateUserRequestBody.pick({
    lastName: true,
    firstName: true,
    email: true,
    phoneNumber: true,
    roleId: true,
});

export type AddUserFormData = z.infer<typeof addUserFormSchema>;

const editUserFormSchema = UpdateUserRequestBody.pick({
    lastName: true,
    firstName: true,
    email: true,
    phoneNumber: true,
    roleId: true,
});

export type EditUserFormData = z.infer<typeof editUserFormSchema>;

type AddUserFormDialogProps = {
    mode: 'add';
    user?: undefined; // Needed for dependency array of useEffect
    onSubmit: (data: AddUserFormData) => Promise<void>;
};

type EditUserFormDialogProps = {
    mode: 'edit';
    user: User | null; // `null` is needed because this dialog cannot be conditionally rendered
    onSubmit: (data: EditUserFormData) => Promise<void>;
};

// Discriminated union
type UserFormDialogProps = {
    userRoles: UserRole[];
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    isSubmitting: boolean;
} & (AddUserFormDialogProps | EditUserFormDialogProps);

export function UserFormDialog({
    isOpen: open,
    onOpenChange,
    isSubmitting,
    userRoles,
    mode,
    user,
    onSubmit,
}: UserFormDialogProps) {
    const defaultValues = (() => {
        switch (mode) {
            case 'add':
                return {
                    lastName: '',
                    firstName: '',
                    email: '',
                    phoneNumber: '',
                    roleId: '' as UserRole['id'],
                };
            case 'edit':
                return {
                    lastName: user?.lastName ?? '',
                    firstName: user?.firstName ?? '',
                    email: user?.email ?? '',
                    phoneNumber: user?.phoneNumber ?? '',
                    roleId: user?.role.id ?? ('' as UserRole['id']),
                };
        }
    })();

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors, isDirty },
    } = useForm<AddUserFormData | EditUserFormData>({
        defaultValues,
        // @ts-expect-error Version mismatch between zod and react-hook-form
        resolver: zodResolver(mode === 'add' ? addUserFormSchema : editUserFormSchema),
    });

    // Sync with user data when dialog opens in edit mode
    useEffect(() => {
        if (open && mode === 'edit' && user) {
            const { lastName, firstName, role, email, phoneNumber } = user;

            reset({
                lastName,
                firstName,
                roleId: role.id,
                email,
                phoneNumber,
            });
        }
    }, [open, mode, user, reset]);

    const handleOpenChange = (isOpen: boolean) => {
        if (isOpen) return;

        if (mode === 'add') {
            reset({
                lastName: '',
                firstName: '',
                roleId: '' as UserRole['id'],
                email: '',
                phoneNumber: '',
            });
        } else {
            reset({
                lastName: user?.lastName ?? '',
                firstName: user?.firstName ?? '',
                roleId: user?.role.id ?? ('' as UserRole['id']),
                email: user?.email ?? '',
                phoneNumber: user?.phoneNumber ?? '',
            });
        }

        onOpenChange(isOpen);
    };

    const title = mode === 'add' ? 'ユーザーの追加' : 'ユーザーの編集';
    const submitLabel = mode === 'add' ? '登録する' : '更新する';
    const pendingLabel = mode === 'add' ? '登録中...' : '更新中...';

    // A bit loosely typed but shouganai
    const onFormSubmit = async (values: AddUserFormData | EditUserFormData) => {
        try {
            if (mode === 'add') {
                await onSubmit(values as AddUserFormData);
            } else {
                await onSubmit(values as EditUserFormData);
            }
            reset();
        } catch {
            // Do nothing (handled by toast notification)
        }
    };

    return (
        <Dialog title={title} open={open} onOpenChange={handleOpenChange}>
            <Form.Root onSubmit={handleSubmit(onFormSubmit)}>
                <Form.Section>
                    <Form.Row>
                        <Form.Field
                            label="氏"
                            required
                            control={(props) => <Input {...props} {...register('lastName')} placeholder="山田" />}
                            error={errors.lastName?.message}
                        />
                        <Form.Field
                            label="名"
                            required
                            control={(props) => <Input {...props} {...register('firstName')} placeholder="太郎" />}
                            error={errors.firstName?.message}
                        />
                    </Form.Row>

                    <Form.Field
                        label="権限"
                        required
                        control={(props) => (
                            <Select {...props} {...register('roleId')}>
                                <option value="">選択してください</option>
                                {userRoles?.map((role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.displayName}
                                    </option>
                                ))}
                            </Select>
                        )}
                        error={errors.roleId?.message}
                    />

                    <Form.Field
                        label="メールアドレス"
                        required
                        control={(props) => (
                            <Input {...props} {...register('email')} type="email" placeholder="example@example.com" />
                        )}
                        error={errors.email?.message}
                    />

                    <Form.Field
                        label="電話番号"
                        control={(props) => (
                            <Input {...props} {...register('phoneNumber')} type="tel" placeholder="09012345678" />
                        )}
                        error={errors.phoneNumber?.message}
                    />
                </Form.Section>

                <DialogButtonGroup>
                    <DialogClose>
                        <Button type="button" variant="secondary">
                            キャンセル
                        </Button>
                    </DialogClose>
                    <Button type="submit" variant="primary" disabled={isSubmitting || !isDirty}>
                        {isSubmitting ? pendingLabel : submitLabel}
                    </Button>
                </DialogButtonGroup>
            </Form.Root>
        </Dialog>
    );
}
